import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { upsertShortstay, listenToShortstayData } from "../../../../redux/shortstaysSlice";
import { TextField, CircularProgress, Box, Button } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const ShortForm8: React.FC = () => {
  const { userId, listingId } = useParams<{ userId: string; listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const currentListing = useSelector((state: RootState) => state.shortstays.currentListing);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [listingId, dispatch]);

  useEffect(() => {
    if (currentListing) {
      setTitle(currentListing.title || "");
      setDescription(currentListing.description || "");
      setNeighborhood(currentListing.neighborhood || "");
    }
  }, [currentListing]);

  const handleSave = async () => {
    if (!listingId) return;
    setIsSaving(true);
    setSuccess(false);
    setError(null);

    try {
      await dispatch(upsertShortstay({
        listingId,
        data: { title, description, neighborhood },
      }));
      setSuccess(true);
      setTimeout(() => {
        setIsSaving(false);
        navigate(`/shortform9/${userId}/${listingId}`);
      }, 1000);
    } catch (err) {
      console.error(err);
      setError("Failed to save. Try again.");
      setTimeout(() => setError(null), 60000);
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/shortform7/${userId}/${listingId}`);
  };

  return (
    <Box sx={{ background: '#000', color: '#fff', minHeight: '100vh', pb: 16 }}>
      {(isSaving || success || error) && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backdropFilter: 'blur(6px)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'
        }}>
          {isSaving && <CircularProgress size={64} sx={{ color: '#fff' }} />}
          {success && <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'limegreen' }} />}
          {error && (
            <Box sx={{ background: '#fff', p: 3, borderRadius: 2, display: 'flex', alignItems: 'center' }}>
              <ErrorOutlineIcon sx={{ fontSize: 60, color: '#f44336', mr: 2 }} />
              <h3 style={{ color: '#f44336', margin: 0 }}>{error}</h3>
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ px: 3, maxWidth: 600, mx: 'auto', pt: 6, pb: 10 }}>
        <h2 style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 30, textAlign: 'center' }}>Describe your place</h2>

        <TextField
          label="Title"
          placeholder="A cozy guesthouse near the lake"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 3, input: { color: '#fff' }, label: { color: '#ccc' }, '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#111', '& fieldset': { borderColor: '#444' }, '&:hover fieldset': { borderColor: '#bf9b30' }, '&.Mui-focused fieldset': { borderColor: '#bf9b30' } } }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Description"
          placeholder="Tell guests about the space, the vibe, and anything else they should know..."
          multiline
          minRows={4}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 3, input: { color: '#fff' }, label: { color: '#ccc' }, '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#111', '& fieldset': { borderColor: '#444' }, '&:hover fieldset': { borderColor: '#bf9b30' }, '&.Mui-focused fieldset': { borderColor: '#bf9b30' } } }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Neighborhood"
          placeholder="Describe what it's like around your place"
          fullWidth
          value={neighborhood}
          onChange={(e) => setNeighborhood(e.target.value)}
          sx={{ mb: 3, input: { color: '#fff' }, label: { color: '#ccc' }, '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#111', '& fieldset': { borderColor: '#444' }, '&:hover fieldset': { borderColor: '#bf9b30' }, '&.Mui-focused fieldset': { borderColor: '#bf9b30' } } }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <Box sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#000', borderTop: '1px solid #333',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        px: 3, py: 2, zIndex: 1000
      }}>
        <Button
          onClick={handleBack}
          sx={{ background: '#222', color: '#fff', borderRadius: 4, px: 4, py: 1, fontWeight: 600 }}
        >
          Back
        </Button>
        <Button
          onClick={handleSave}
          sx={{
            background: 'linear-gradient(to right, #fff, #bf9b30)',
            color: '#000',
            borderRadius: 4,
            px: 4,
            py: 1,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          NEXT
        </Button>
      </Box>
    </Box>
  );
};

export default ShortForm8;
